// Content for the footer/marketing pages. Copy lives here so pages stay thin.

export const CONNECTORS = {
  groups: [
    {
      title: "Databases",
      items: [
        { name: "PostgreSQL", status: "available" },
        { name: "MySQL", status: "available" },
        { name: "MariaDB", status: "available" },
        { name: "SQLite", status: "available" },
        { name: "SQL Server", status: "soon" },
      ],
    },
    {
      title: "Warehouses & lakehouses",
      items: [
        { name: "ClickHouse", status: "beta" },
        { name: "Amazon Redshift", status: "beta" },
        { name: "Snowflake", status: "soon" },
        { name: "Google BigQuery", status: "soon" },
        { name: "Databricks", status: "soon" },
      ],
    },
    {
      title: "Advanced",
      items: [
        { name: "Any SQLAlchemy URI", status: "available" },
        { name: "dbt metadata", status: "soon" },
      ],
    },
  ],
  statusLabels: {
    available: { label: "Available", color: "#0FAE96" },
    beta: { label: "Beta", color: "#F59E0B" },
    soon: { label: "On the roadmap", color: "#64748B" },
  },
};

export const ROADMAP = {
  columns: [
    {
      tag: "Shipping now",
      accent: "#0FAE96",
      items: [
        "PostgreSQL connector + schema introspection",
        "AI-generated glossary & sensitivity tagging",
        "Inferred + verified semantic relationship graph",
        "Governed metrics with one source of truth",
        "Plain-English chat with SQL + audit log",
        "Developers API (workspace-scoped keys)",
        "Industry packs: Healthcare, SaaS, FinServ, Retail",
      ],
    },
    {
      tag: "Up next",
      accent: "#3B82F6",
      items: [
        "Snowflake & BigQuery general availability",
        "Scheduled reports & alerts (email + Slack)",
        "SSO / SAML for Business & Enterprise",
        "Role-based row-level policies",
        "Saved metric versioning & change review",
      ],
    },
    {
      tag: "Later",
      accent: "#8B5CF6",
      items: [
        "Databricks & lakehouse connectors",
        "dbt model sync (two-way)",
        "Self-hosted / VPC deployment",
        "Multi-language semantic search",
      ],
    },
  ],
};

export const ABOUT = {
  mission:
    "Enterprises are pouring AI on top of databases it can't actually reason about — undocumented columns, ambiguous joins and no governance. Sema is the missing layer that gives your data a vocabulary, so AI answers are correct, explainable and safe.",
  story: [
    "Sema started from a simple, repeated frustration: every \"AI on your data\" demo fell apart the moment it met a real warehouse with 1,200 cryptically named tables and no shared definition of a single metric.",
    "We believe the answer isn't a smarter guess — it's a governed semantic layer the AI is forced to reason through. Define what your data means once, and every answer, report and alert resolves the same way, with the SQL and the audit trail to prove it.",
  ],
  principles: [
    { icon: "ShieldCheck", title: "Governed by default", body: "Access control, masking and an audit log are on from the first query — not a future enterprise add-on." },
    { icon: "Eye", title: "Always explainable", body: "Every answer ships with the SQL it ran and the tables it touched. No black boxes." },
    { icon: "Layers", title: "One source of truth", body: "A metric means one thing across chat, reports, alerts and the API." },
    { icon: "Gauge", title: "Built for scale", body: "Designed for warehouses with thousands of tables, not toy demos." },
  ],
  stats: [
    { stat: "2025", label: "founded" },
    { stat: "Remote", label: "first" },
    { stat: "1,200+", label: "tables modeled in a single workspace" },
  ],
};

export const CUSTOMERS = {
  featured: {
    name: "Cedar Grove Senior Living",
    industry: "Healthcare",
    quote:
      "Our operators ask Sema about readmissions and occupancy in plain English and trust the number — because every answer shows the SQL and respects who's allowed to see PHI.",
    person: "VP of Operations, Cedar Grove",
    metrics: [
      { stat: "12", label: "clinical + ops tables unified" },
      { stat: "100%", label: "PHI auto-flagged & masked" },
      { stat: "min", label: "from question to charted answer" },
    ],
  },
  quotes: [
    { quote: "We deleted three conflicting definitions of churn in a week. Now there's just one.", person: "Head of Data, SaaS design partner" },
    { quote: "The audit log alone made our risk team comfortable putting AI in front of finance data.", person: "Director of Analytics, lending design partner" },
    { quote: "Merchandisers self-serve sell-through across channels without pinging us. That's the dream.", person: "Analytics Lead, retail design partner" },
  ],
  note:
    "These quotes come from our design-partner program. We publish company names only with written permission — happy to arrange reference calls in your industry.",
  logos: ["Senior living", "B2B SaaS", "Consumer lending", "Retail & e-commerce", "Manufacturing", "Logistics"],
};

export const CAREERS = {
  intro:
    "We're a small, senior, remote-first team building the governance layer for enterprise AI. We hire for ownership, taste and clarity of thought.",
  values: [
    { icon: "Compass", title: "Own the outcome", body: "Small team, high trust. You'll own problems end-to-end, not tickets." },
    { icon: "Sparkles", title: "Taste matters", body: "We sweat the details in product, code and writing." },
    { icon: "Users", title: "Customer-obsessed", body: "We sit with data teams and build what actually unblocks them." },
  ],
  roles: [
    { title: "Founding Product Engineer", team: "Engineering", location: "Remote", type: "Full-time" },
    { title: "Semantic Modeling Engineer", team: "Engineering", location: "Remote", type: "Full-time" },
    { title: "Founding Account Executive", team: "Go-to-market", location: "Remote (US)", type: "Full-time" },
  ],
  email: "careers@semalayer.com",
};

export const CHANGELOG = [
  {
    version: "v1.6", date: "2026-06-22", tags: ["Marketing", "Lead-gen"],
    items: [
      "New industry solution pages for Healthcare, SaaS, Financial Services and Retail.",
      "Product walkthrough page with live product screenshots and a clear differentiation section.",
      "Lead capture with double opt-in confirmation and built-in spam protection.",
    ],
  },
  {
    version: "v1.5", date: "2026-06-10", tags: ["Platform"],
    items: [
      "Fresh-database demo seeding: a new deployment boots demo-ready automatically.",
      "Concise schema answers and sharper entity ranking for very large databases.",
      "Focused, navigable relationship graph view.",
    ],
  },
  {
    version: "v1.4", date: "2026-05-28", tags: ["Developers", "Governance"],
    items: [
      "Developers API with workspace-scoped, bcrypt-hashed keys (POST /v1/query).",
      "Save any answer as a governed metric directly from the chat preview.",
      "Connect-time table filtering for large Postgres schemas.",
    ],
  },
  {
    version: "v1.3", date: "2026-05-12", tags: ["Core"],
    items: [
      "AI glossary generation with sensitivity & lifecycle tagging.",
      "Inferred relationship graph with confidence scoring and one-click verify.",
      "Append-only audit log with CSV export.",
    ],
  },
];

export const TRUST = {
  intro:
    "Sema is built so you can put AI in front of sensitive data with confidence. Governance, access control and auditability are core to the product — not bolt-ons.",
  cards: [
    { icon: "BadgeCheck", title: "SOC 2 Type II", body: "In progress. Reach out for our current report status and timeline." },
    { icon: "Globe", title: "GDPR-ready", body: "Data processing addendum available; EU data handling on request." },
    { icon: "HeartPulse", title: "HIPAA on request", body: "PHI is auto-classified and masked by role; BAA available for eligible plans." },
    { icon: "Lock", title: "Encryption", body: "Encrypted in transit (TLS) and at rest. Credentials are never logged." },
    { icon: "Users", title: "Access control", body: "Role-based access, restricted-column masking, and per-workspace isolation." },
    { icon: "ScrollText", title: "Audit log", body: "Every query, plan and config change is recorded to an append-only log." },
  ],
  disclosure: "security@semalayer.com",
};

export const STATUS = {
  overall: "All systems operational",
  components: [
    { name: "API", uptime: "99.98%" },
    { name: "Web application", uptime: "99.99%" },
    { name: "Query engine", uptime: "99.95%" },
    { name: "Authentication", uptime: "100.00%" },
    { name: "Email delivery", uptime: "99.97%" },
  ],
  note: "Live status reflects the last 90 days. For incident history or to subscribe to updates, contact support@semalayer.com.",
};

export const CONTACT = {
  channels: [
    { icon: "Briefcase", title: "Talk to sales", body: "See Sema on your own data and get pricing for your team.", action: "Use the form", email: "sales@semalayer.com" },
    { icon: "LifeBuoy", title: "Support", body: "Already a customer and need a hand? We're here.", email: "support@semalayer.com" },
    { icon: "ShieldAlert", title: "Security", body: "Report a vulnerability or ask about our security posture.", email: "security@semalayer.com" },
  ],
};

// Feature pages (Discovery, Governance) — concise, screenshot-backed.
export const FEATURES = {
  discovery: {
    slug: "discovery",
    eyebrow: "Discovery",
    accent: "#0FAE96",
    metaTitle: "Automatic Data Discovery & Glossary | Sema",
    metaDescription:
      "Sema introspects your warehouse, drafts a plain-English glossary for every table and column, tags sensitivity, and infers the relationship graph — so your data is understandable in minutes.",
    keywords: "data discovery, data glossary, schema introspection, data catalog, semantic graph",
    h1: "Understand your warehouse in minutes.",
    subtitle:
      "Point Sema at your database and it reads every table, drafts a plain-English definition for each column, tags what's sensitive, and infers how everything joins — so nobody starts from a blank schema again.",
    blocks: [
      { icon: "ScanSearch", title: "Schema introspection", body: "Tables, columns, types and keys read directly from your warehouse in seconds." },
      { icon: "BookOpen", title: "AI glossary", body: "A drafted definition for every table and column, with sensitivity and lifecycle tags you confirm." },
      { icon: "Workflow", title: "Relationship graph", body: "Inferred joins with confidence scores — verify once and every future answer is correct." },
    ],
    shots: [
      { src: "/screenshots/glossary.png", caption: "Auto-generated, human-confirmed definitions with sensitivity & lifecycle." },
      { src: "/screenshots/semantics.png", caption: "Inferred vs. verified joins, navigable as a graph." },
    ],
  },
  governance: {
    slug: "governance",
    eyebrow: "Governance",
    accent: "#3B82F6",
    metaTitle: "Data Governance for AI — Access, Masking & Audit | Sema",
    metaDescription:
      "Role-based access, restricted-column masking and an append-only audit log are on by default in Sema — so every AI answer is safe to share and easy to defend.",
    keywords: "data governance, AI governance, audit log, role based access control, column masking, data security",
    h1: "Governance that's on by default.",
    subtitle:
      "Sema enforces who can see what before any SQL runs, masks restricted columns by role, and records every question, plan and answer — so AI on sensitive data is safe to share and easy to audit.",
    blocks: [
      { icon: "Lock", title: "Role-based access", body: "Permissions enforced at plan time, per workspace and per role — before a query executes." },
      { icon: "EyeOff", title: "Column masking", body: "PII and restricted fields are refused or masked automatically based on the asker's role." },
      { icon: "ScrollText", title: "Append-only audit", body: "Every query, plan and config change is logged immutably and exportable to CSV." },
    ],
    shots: [
      { src: "/screenshots/audit.png", caption: "Append-only audit log — who asked what, when, and what ran." },
    ],
  },
};
